import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'

import { handleOpenInNewTab } from '../../services/articleCardHandlers'
import { useAuth } from '../../context/AuthContext'
import {
  Box,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  Fab,
  Zoom,
  IconButton,
  Modal,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import { parseHtmlSanitizeAddTargetToLinks } from '../../services/utils'
import Article from './Article'
import useFindScopeLayer from '../../hooks/useFindScopeLayer'

const ArticleCard = ({ article, searchQuery, showSnackbar }) => {
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('') // Feedback submitted successfully 😄
  const [snackbarSeverity, setSnackbarSeverity] = useState('success') // success or error

  // Scope browser Ctrl+F to this modal's content while it is open.
  useFindScopeLayer(modalIsOpen, 'card-modal')

  // We can't render Snackbar by mentioning it in FeedbackForm component, why?? So Move Snackbar to a Higher Level (Optional): The Snackbar might be constrained by the modal or other parent elements, so one option is to lift it up to a higher level in the React tree. However, if the modal isn't an issue, we can work with what you have and ensure the Snackbar is triggered properly.
  //       <Modal /> present in <ArticleCard /> , and inside Modal we have <Article />, and in Article we have <FeedbackForm /> which is try to render <Snackbar />
  // The modal's rendering behavior could interfere with how the Snackbar is displayed. Snackbars usually render on the top of the DOM tree, and being inside a modal might restrict its visibility due to the modal's context and z-index behavior.

  // const componentRef = useRef();
  const modalContentRef = useRef(null)

  // Theme colors
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
    lightGray: '#f5f5f5',
  }

  // Consistent button border radius
  const buttonBorderRadius = '28px'

  // Format the path string to show full hierarchy in a readable way
  const formatPathString = (path) => {
    if (!path) return ''

    // Split by common path separators
    const pathParts = path.split(/[\/\\-_]/)

    // Format each part of the path
    const formattedParts = pathParts.map((part) => {
      if (!part) return ''

      // Handle common patterns like "chapter3"
      const match = part.match(
        /^(chapter|part|section|division|title)s?(\d+)$/i
      )
      if (match) {
        // Capitalize properly and add spacing
        return (
          match[1].charAt(0).toUpperCase() + match[1].slice(1) + ' ' + match[2]
        )
      }

      // Handle camelCase
      let formatted = part.replace(/([a-z])([A-Z])/g, '$1 $2')

      // Add a space between any letter followed by number
      formatted = formatted.replace(/([a-zA-Z])(\d)/g, '$1 $2')

      // Capitalize first letter of each word
      formatted = formatted
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')

      return formatted
    })

    // Join with forward slashes for better visual hierarchy
    return formattedParts.filter((part) => part).join(' / ')
  }

  const handleModalToggle = useCallback(() => {
    // Check if user is authenticated before opening modal
    if (!isAuthenticated) {
      handleOpenInNewTab(article, `/articles/${article?.slug}`, showSnackbar)
      return
    }
    setModalIsOpen((prev) => !prev)
    // handleOpenInNewTab(article, `/articles/${article?.slug}`, showSnackbar)
  }, [article, isAuthenticated, showSnackbar])

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return
    setSnackbarOpen(false)
  }

  const handleShowSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message || '')
    setSnackbarSeverity(severity || 'info')
    setSnackbarOpen(true)
  }, [])

  // Handle ESC key press - close modal only if no popups are open
  // Popup closing order: NestedPopup (top) -> MainPopup -> Modal (bottom)
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && modalIsOpen) {
        // Check if NestedPopup is open (highest priority - closes first)
        const nestedPopup = document.getElementById('nested-popup-container')
        if (nestedPopup) {
          // NestedPopup is open, let it handle its own ESC key
          return
        }
        
        // Check if MainPopup is open (second priority)
        const mainPopup = document.getElementById('main-popup-container')
        if (mainPopup) {
          // MainPopup is open, let it handle its own ESC key
          return
        }
        
        // No popups are open, close the Modal
        handleModalToggle()
      }
    }

    if (modalIsOpen) {
      document.addEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [modalIsOpen, handleModalToggle])

  // Set up scroll detection with direct DOM approach
  useEffect(() => {
    // Only run this if modal is open
    if (!modalIsOpen) return

    // Use requestAnimationFrame for smoother detection
    let scrollFrame
    const checkScroll = () => {
      if (modalContentRef.current) {
        // Check if scrolled down enough
        setShowBackToTop(modalContentRef.current.scrollTop > 300)
      }
      // Continue checking
      scrollFrame = requestAnimationFrame(checkScroll)
    }

    // Start the animation frame loop
    scrollFrame = requestAnimationFrame(checkScroll)

    // Add a basic scroll listener as backup
    const handleScroll = () => {
      if (modalContentRef.current) {
        setShowBackToTop(modalContentRef.current.scrollTop > 300)
      }
    }

    // Get the modal element
    const modalElement = modalContentRef.current
    if (modalElement) {
      modalElement.addEventListener('scroll', handleScroll)
    }

    // Clean up
    return () => {
      cancelAnimationFrame(scrollFrame)
      if (modalElement) {
        modalElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [modalIsOpen])

  // Function to scroll modal to top
  const scrollToTop = useCallback(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [])

  // Truncate long text
  // const truncateText = (text, maxLength) => {
  //   if (!text) return ''
  //   if (text.length <= maxLength) return text
  //   return text.slice(0, maxLength) + '...'
  // }

  // TODO1: updated version of truncateText is present only in ArticleCard and not in DecisionCard, GuideCard, TaxTreatyCard
  // issue: any html code should be ignored while displaying text of title/name/country2Name,  for e.g. "Article 26 - <a href='/decisions/uae-cit-fdl-47-2022-md-132-of-2023' target='_blank' rel='noopener noreferrer'>Transfers Within a Qualifying Group</a>" so this should be considered as ""Article 26 - Transfers Within a Qualifying Group" before using slicing,  but we dont want to remove the effect html tags were giving like bold italic text color change etc
  // TODO2: also tooltip should not show html code while hovering over title
  const truncateText = (html, maxLength) => {
    if (!html) return ''

    const div = document.createElement('div')
    div.innerHTML = html

    let visibleCount = 0
    let truncated = ''

    const walk = (node) => {
      if (visibleCount >= maxLength) return

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent
        const remaining = maxLength - visibleCount

        if (text.length <= remaining) {
          truncated += text
          visibleCount += text.length
        } else {
          truncated += text.slice(0, remaining) + '...'
          visibleCount = maxLength
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        truncated += `<${node.nodeName.toLowerCase()}${[...node.attributes]
          .map((attr) => ` ${attr.name}="${attr.value}"`)
          .join('')}>`
        for (let child of node.childNodes) walk(child)
        truncated += `</${node.nodeName.toLowerCase()}>`
      }
    }

    for (let child of div.childNodes) {
      if (visibleCount < maxLength) walk(child)
    }

    return truncated
  }

  // Get plain text version of the title for tooltip
  const getPlainText = (html) => {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  // Adjust title truncation length based on whether path is shown
  const hasPath = article?.pathString && article.pathString.trim() !== ''
  const titleTruncateLength = hasPath
    ? isMobileScreen
      ? 70
      : 100 // Shorter title when path is shown
    : isMobileScreen
    ? 90
    : 120 // Original lengths when no path

  // Calculate card height based on screen size
  const cardHeight = isMobileScreen
    ? '120px' // smaller screen
    : isMediumScreen
    ? '140px' // medium screen
    : '140px' // large screen

  // Create truncated versions and check if tooltips are needed
  const truncatedTitle = truncateText(article?.title, isMobileScreen ? 90 : 120)
  const plainTextTitle = getPlainText(article?.title)
  const truncatedName = truncateText(article?.name, isMobileScreen ? 60 : 80)
  const plainTextName = getPlainText(article?.name)
  const showTitleTooltip = plainTextTitle.length > (isMobileScreen ? 90 : 120)
  const showNameTooltip = plainTextName.length > (isMobileScreen ? 60 : 80)

  return (
    <>
      <Paper
        onClick={handleModalToggle}
        sx={{
          margin: 2,
          padding: 2,
          cursor: 'pointer',
          // height: cardHeight,

          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        }}
        id={`article-${article?.slug}`}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {/* Main content area */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: article?.name ? 'space-between' : 'center', // Center title when no name
                flexGrow: 1,
                overflow: 'hidden',
                mr: isMobileScreen ? 1 : 2,
              }}
            >
              {/* Path string indicator with width that fits content */}
              {hasPath && (
                <Box sx={{ mb: 0.7 }}>
                  <Tooltip title={formatPathString(article.pathString)} arrow>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: '100%',
                        borderRadius: '4px',
                        bgcolor: '#f0f4f9',
                        px: 1,
                        py: 0.3,
                        overflow: 'hidden',
                      }}
                    >
                      <MenuBookOutlinedIcon
                        sx={{
                          fontSize: isMobileScreen ? '0.9rem' : '1rem',
                          color: themeColors.primary,
                          flexShrink: 0,
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        variant='caption'
                        noWrap
                        sx={{
                          color: themeColors.primary,
                          fontWeight: 600,
                          fontSize: isMobileScreen ? '0.65rem' : '0.75rem',
                          letterSpacing: '0.01em',
                        }}
                      >
                        {formatPathString(article.pathString)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              )}
              {/* Beta Version indicator */}
              {article?.betaVersion && (
                <Box sx={{ mb: 0.7 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      maxWidth: '100%',
                      borderRadius: '4px',
                      bgcolor: '#e3f2fd',
                      px: 1,
                      py: 0.3,
                      border: '1px solid rgba(25, 118, 210, 0.25)',
                    }}
                  >
                    <Typography
                      variant='caption'
                      sx={{
                        color: '#1976d2',
                        fontWeight: 600,
                        fontSize: isMobileScreen ? '0.65rem' : '0.75rem',
                        letterSpacing: '0.01em',
                      }}
                    >
                      Beta Version
                    </Typography>
                  </Box>
                </Box>
              )}
              {/* Main heading - Title (swapped from name) */}
              <Tooltip
                title={showTitleTooltip ? plainTextTitle : ''}
                arrow
                placement={article?.name ? 'top' : 'bottom'}
              >
                <Typography
                  variant={isMobileScreen ? 'subtitle1' : 'h6'}
                  sx={{
                    cursor: 'pointer',
                    color: themeColors.primary,
                    lineHeight: isMobileScreen ? 1.3 : 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: article?.name ? 2 : 3, // More lines if no name
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    mb: article?.name ? 1 : 0, // No bottom margin if no name follows
                    fontWeight: 600,
                    fontSize: isMobileScreen ? '1rem' : undefined,
                    '& a': {
                      pointerEvents: 'none',
                      color: 'inherit',
                      textDecoration: 'inherit',
                    },
                  }}
                >
                  {parseHtmlSanitizeAddTargetToLinks(
                    truncatedTitle,
                    searchQuery
                  )}
                </Typography>
              </Tooltip>
              {/* Subheading - Name (swapped from title) */}
              {truncatedName && (
                <Tooltip
                  title={showNameTooltip ? plainTextName : ''}
                  arrow
                  placement='bottom'
                >
                  <Typography
                    variant='body2'
                    sx={{
                      cursor: 'pointer',
                      color: 'text.secondary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.3,
                      fontSize: isMobileScreen ? '0.75rem' : '0.875rem',
                      mb: 1,
                      '& a': {
                        pointerEvents: 'none',
                        color: 'inherit',
                        textDecoration: 'inherit',
                      },
                    }}
                  >
                    {parseHtmlSanitizeAddTargetToLinks(
                      truncatedName,
                      searchQuery
                    )}
                  </Typography>
                </Tooltip>
              )}
            </Box>
            {/* Right side button */}
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Tooltip title='Open in new tab' arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenInNewTab(article, `/articles/${article?.slug}`)
                  }}
                  size={isMobileScreen ? 'small' : 'medium'}
                  sx={{
                    color: themeColors.primary,
                    '&:hover': {
                      backgroundColor: 'rgba(35, 37, 54, 0.08)',
                    },
                  }}
                >
                  <OpenInNewRoundedIcon
                    fontSize={isMobileScreen ? 'small' : 'medium'}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Modal open={modalIsOpen} onClose={handleModalToggle} disableEscapeKeyDown disableEnforceFocus>
          {/* Wrapper Box to maintain border radius when scrollbar appears */}
          <Box
            data-find-scope='card-modal'
            onClick={(e) => e.stopPropagation()}
            sx={{
              margin: '5vh auto',
              width: isMobileScreen ? '95vw' : isMediumScreen ? '90vw' : '85vw',
              maxHeight: '90vh',
              borderRadius: '8px',
              overflow: 'hidden', // Hide any content that would break the border radius
              position: 'relative',
              display: 'flex', // Use flexbox
              flexDirection: 'column', // Stack content vertically
              backgroundColor: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            {/* Content Box with scrolling */}
            <Box
              ref={modalContentRef}
              sx={{
                padding: '1px 6px 16px 6px', // Add padding inside the content area
                paddingTop: 2,
                position: 'relative',
                overflowY: 'auto', // Enable vertical scrolling
                flexGrow: 1, // Allow this box to expand
                // Ensure scrollbar doesn't affect layout
                scrollbarWidth: 'thin', // For Firefox
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '0 8px 8px 0', // Round the scrollbar track
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {/* RESPONSIVE CLOSE BUTTON */}
              {isMobileScreen ? (
                // Smaller close button for mobile
                <IconButton
                  onClick={handleModalToggle}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    float: 'right',
                    zIndex: 1500,
                    backgroundColor: 'white',
                    border: '1px solid #232536',
                    margin: '-8px -8px 0 0',
                    right: '-2px', // Use right positioning for exact alignment
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  }}
                  aria-label='close modal'
                  size='small'
                >
                  <CloseIcon sx={{ color: '#232536' }} fontSize='small' />
                </IconButton>
              ) : (
                // Larger close button with text for desktop/tablet
                <Box
                  sx={{
                    position: 'sticky',
                    cursor: 'pointer',
                    top: 0,
                    float: 'right',
                    zIndex: 1500,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    border: '1px solid #232536',
                    borderRadius: buttonBorderRadius,
                    margin: '-4px -4px 0 0',
                    right: 0,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    padding: '4px 12px',
                  }}
                  onClick={handleModalToggle}
                  role='button'
                  aria-label='close modal'
                >
                  <CloseIcon
                    sx={{ color: themeColors.primary }}
                    fontSize='small'
                  />
                  <Box sx={{ alignSelf: 'center' }}>
                    <Typography
                      variant='button'
                      sx={{
                        color: themeColors.primary,
                        marginLeft: 0.5,
                        fontSize: '0.80rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Close
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Conditionally render Article component only if authenticated */}
              {isLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    padding: 4,
                  }}
                >
                  <CircularProgress sx={{ color: themeColors.primary }} />
                  <Typography
                    variant='body2'
                    sx={{
                      mt: 2,
                      color: 'text.secondary',
                    }}
                  >
                    Checking authentication...
                  </Typography>
                </Box>
              ) : isAuthenticated ? (
                <Article
                  article={article}
                  searchQuery={searchQuery}
                  showSnackbar={handleShowSnackbar}
                  isInModal={true}
                  parentContainerRef={modalContentRef}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    padding: 4,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: themeColors.primary,
                    }}
                  >
                    Authentication Required
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      mb: 3,
                      color: 'text.secondary',
                      maxWidth: '500px',
                    }}
                  >
                    Please log in to view this article. You need to be authenticated
                    to access article content.
                  </Typography>
                  <Button
                    variant='contained'
                    onClick={() => {
                      // Store current path for redirect after login
                      sessionStorage.setItem(
                        'redirectPath',
                        `/articles/${article?.slug}`
                      )
                      navigate('/login')
                    }}
                    sx={{
                      backgroundColor: themeColors.primary,
                      color: 'white',
                      padding: '10px 24px',
                      borderRadius: buttonBorderRadius,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: themeColors.hover,
                      },
                    }}
                  >
                    Go to Login
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: isMobileScreen ? 20 : 25,
                  // right: 12, // Adjusted to account for scrollbar
                  float: 'right',
                  marginTop: '-60px',
                  zIndex: 1400,
                }}
              >
                <Zoom in={showBackToTop}>
                  {isMobileScreen ? (
                    // Small circular Fab for mobile
                    <Fab
                      size='small'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: '#232536',
                        color: 'white',
                        '&:hover': { backgroundColor: '#3a3e5a' },
                      }}
                    >
                      <KeyboardArrowUpIcon fontSize='small' />
                    </Fab>
                  ) : (
                    // Extended Fab with text for desktop/tablet
                    <Fab
                      variant='extended'
                      size='medium'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': { backgroundColor: themeColors.hover },
                        paddingLeft: 2,
                        paddingRight: 2,
                        borderRadius: buttonBorderRadius,
                      }}
                    >
                      <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                      <Typography
                        variant='button'
                        sx={{
                          // color: themeColors.primary,
                          // marginLeft: 0.5,
                          // fontSize: '0.80rem',
                          fontWeight: 'bold',
                        }}
                      >
                        Back to Top
                      </Typography>
                    </Fab>
                  )}
                </Zoom>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Paper>
      {/* Snackbar outside of Modal */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            // backgroundColor:
            //   snackbarSeverity === 'warning'
            //     ? '#ffcf51'
            //     : snackbarSeverity === 'error'
            //     ? '#d32f2f'
            //     : snackbarSeverity === 'success'
            //     ? '#43a047'
            //     : '#232536',
            // color: snackbarSeverity === 'warning' ? '#232536' : '#fff',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default ArticleCard
